import React, { useState } from 'react';
import { toast } from 'sonner';
import { BACKEND_URL } from '../config/api';
import { getApiHeaders } from '../utils/auth';
import { useAccessControl } from '../hooks/useAccessControl';
import { Input } from './ui/input';
import { Button } from './ui/button';

/**
 * VerificationBanner - Shows a persistent banner for users who haven't verified their email
 * 
 * Features:
 * - Dismissable (per session, comes back on refresh)
 * - Shows resend verification email button
 * - Inline OTP input for quick verification without leaving the page
 * - Explains which features are restricted
 * - Links to Settings for full verification UI
 * - Only shown to account OWNERS (team members don't need to verify)
 * 
 * Uses unified access control for visibility decisions.
 */
const VerificationBanner = ({ user, onVerificationSent, onVerificationComplete }) => {
  const [isDismissed, setIsDismissed] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const { canSee } = useAccessControl();

  // Use centralized access control for visibility
  // Don't show if user is verified, banner is dismissed, or access control says hide
  if (!user || user.emailVerified || isDismissed || !canSee('toast.emailVerify')) {
    return null;
  }

  const handleResendVerification = async () => {
    if (resendCooldown > 0) {
      toast.info(`Please wait ${resendCooldown} seconds before resending`);
      return;
    }

    setIsResending(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/resend-verification-email`, {
        method: 'POST',
        headers: getApiHeaders(),
        body: JSON.stringify({ email: user.email }),
      });

      const result = await response.json();

      if (response.ok) {
        toast.success('Verification email sent! Check your inbox.');
        setResendCooldown(60);
        
        // Start countdown
        const interval = setInterval(() => {
          setResendCooldown((prev) => {
            if (prev <= 1) {
              clearInterval(interval);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);

        if (onVerificationSent) {
          onVerificationSent();
        }
      } else {
        toast.error(result.message || 'Failed to send verification email');
      }
    } catch {
      toast.error('Network error. Please try again.');
    }

    setIsResending(false);
  };

  const handleVerifyOtp = async () => {
    if (!otpCode || otpCode.length !== 6) {
      toast.error('Please enter a valid 6-digit verification code');
      return;
    }

    setIsVerifying(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/verify-email`, {
        method: 'POST',
        headers: getApiHeaders(),
        body: JSON.stringify({ 
          email: user.email,
          otp: otpCode 
        }),
      });

      const result = await response.json();

      if (response.ok) {
        toast.success('Email verified successfully! 🎉');
        setShowOtpInput(false);
        setOtpCode('');
        
        // Notify parent component to refresh user data
        if (onVerificationComplete) {
          onVerificationComplete(result);
        }
      } else {
        toast.error(result.message || 'Invalid verification code. Please try again.');
      }
    } catch {
      toast.error('Network error. Please try again.');
    }

    setIsVerifying(false);
  };

  return (
    <div className="bg-amber-50 border-b border-amber-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex flex-col gap-3">
          {/* Main banner row */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-start sm:items-center gap-3">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                  <i className="fas fa-envelope text-amber-600 text-sm"></i>
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-amber-800">
                  Verify your email to unlock all features
                </p>
                <p className="text-xs text-amber-600 mt-0.5">
                  Some features like billing, exports, and team management require a verified email.
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2 ml-11 sm:ml-0">
              {!showOtpInput ? (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      handleResendVerification();
                      setShowOtpInput(true);
                    }}
                    disabled={isResending || resendCooldown > 0}
                    className="border-amber-300 text-amber-700 hover:bg-amber-100 text-xs px-3 py-1.5 h-auto"
                  >
                    {isResending ? (
                      <>
                        <div className="w-3 h-3 border-2 border-amber-300 border-t-amber-600 rounded-full animate-spin mr-1.5"></div>
                        Sending...
                      </>
                    ) : resendCooldown > 0 ? (
                      `Resend in ${resendCooldown}s`
                    ) : (
                      <>
                        <i className="fas fa-paper-plane mr-1.5"></i>
                        Send Code
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowOtpInput(true)}
                    className="border-amber-300 text-amber-700 hover:bg-amber-100 text-xs px-3 py-1.5 h-auto"
                  >
                    <i className="fas fa-key mr-1.5"></i>
                    Enter Code
                  </Button>
                </>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowOtpInput(false);
                    setOtpCode('');
                  }}
                  className="text-amber-600 hover:text-amber-800 text-xs px-2 py-1 h-auto"
                >
                  <i className="fas fa-chevron-up mr-1"></i>
                  Hide
                </Button>
              )}
              
              <button
                onClick={() => setIsDismissed(true)}
                className="text-amber-400 hover:text-amber-600 p-1 rounded"
                aria-label="Dismiss banner"
              >
                <i className="fas fa-times text-sm"></i>
              </button>
            </div>
          </div>

          {/* OTP Input Row - shown when user clicks "Enter Code" */}
          {showOtpInput && (
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 ml-11 sm:ml-11 animate-in slide-in-from-top-2 duration-200">
              <div className="flex items-center gap-2 flex-1 max-w-md">
                <Input
                  type="text"
                  placeholder="Enter 6-digit code"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="h-9 text-center text-lg tracking-widest font-mono border-amber-300 focus:border-amber-500 focus:ring-amber-500"
                  maxLength={6}
                  data-testid="otp-input"
                />
                <Button
                  size="sm"
                  onClick={handleVerifyOtp}
                  disabled={isVerifying || otpCode.length !== 6}
                  className="bg-amber-600 hover:bg-amber-700 text-white px-4 h-9"
                  data-testid="verify-otp-button"
                >
                  {isVerifying ? (
                    <>
                      <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin mr-1.5"></div>
                      Verifying...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-check mr-1.5"></i>
                      Verify
                    </>
                  )}
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-amber-600">Didn't get it?</span>
                <button
                  onClick={handleResendVerification}
                  disabled={isResending || resendCooldown > 0}
                  className={`text-xs font-medium ${
                    resendCooldown > 0 
                      ? 'text-amber-400 cursor-not-allowed' 
                      : 'text-amber-700 hover:text-amber-900 underline'
                  }`}
                >
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerificationBanner;
